import { useState, useEffect, useCallback, useMemo } from 'react';
import { Crosshair, Plus, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContextSupabase';
import SearchModal, { type SearchParams } from '../components/prospection/SearchModal';
import LeadsTable, { type Lead } from '../components/prospection/LeadsTable';
import LeadSidePanel from '../components/prospection/LeadSidePanel';
import LeadFilters from '../components/prospection/LeadFilters';

interface LeadSearch {
  id: string;
  name: string;
  status: string;
  total_results: number;
  created_at: string;
  error_message: string;
}

interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
}

export default function Prospection() {
  const { currentUser, userProfile } = useAuth();
  const agencyId = userProfile?.agency_id || '';

  const [leads, setLeads] = useState<Lead[]>([]);
  const [searches, setSearches] = useState<LeadSearch[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [isSubmittingSearch, setIsSubmittingSearch] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  const loadAll = useCallback(async () => {
    if (!agencyId) return;
    setLoading(true);

    const [leadsRes, searchesRes, teamRes] = await Promise.all([
      supabase.from('leads').select('*').eq('agency_id', agencyId).order('score', { ascending: false }),
      supabase.from('lead_searches').select('*').eq('agency_id', agencyId).order('created_at', { ascending: false }),
      supabase.from('users').select('id, first_name, last_name').eq('agency_id', agencyId),
    ]);

    const allLeads = (leadsRes.data || []) as Lead[];
    setLeads(allLeads);
    setSearches((searchesRes.data || []) as LeadSearch[]);
    setTeamMembers((teamRes.data || []) as TeamMember[]);

    setLoading(false);
  }, [agencyId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const running = searches.filter((s) => s.status === 'running');
    if (running.length === 0) return;

    const syncRunning = async () => {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) return;

      for (const s of running) {
        try {
          const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/lead-search-sync`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({ searchId: s.id }),
          });
          const result = await res.json();
          if (result.status !== 'running') {
            loadAll();
            return;
          }
        } catch {}
      }
    };

    syncRunning();
    const interval = setInterval(syncRunning, 20000);
    return () => clearInterval(interval);
  }, [searches, loadAll]);

  const handleStartSearch = async (params: SearchParams) => {
    if (!currentUser) return;
    setIsSubmittingSearch(true);

    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/lead-search-start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(params),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Erreur');
      }

      setShowSearchModal(false);
      loadAll();
    } catch (err: any) {
      alert(err.message || 'Erreur lors du lancement de la recherche');
    } finally {
      setIsSubmittingSearch(false);
    }
  };

  const handleStageChange = async (leadId: string, stage: string) => {
    const lead = leads.find((l) => l.id === leadId);
    const oldStage = lead?.stage || 'new';

    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, stage } : l)));
    if (selectedLead?.id === leadId) setSelectedLead((prev) => prev ? { ...prev, stage } : prev);

    await supabase.from('leads').update({ stage }).eq('id', leadId);

    const userName = [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ') || 'Utilisateur';
    await supabase.from('lead_activity').insert({
      lead_id: leadId,
      user_id: currentUser?.id,
      user_name: userName,
      type: 'stage_change',
      payload_json: { from: oldStage, to: stage },
    });

    loadAll();
  };

  const handleOwnerChange = async (leadId: string, ownerId: string | null) => {
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, owner_id: ownerId } : l)));
    await supabase.from('leads').update({ owner_id: ownerId }).eq('id', leadId);

    const member = teamMembers.find((m) => m.id === ownerId);
    const userName = [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ') || 'Utilisateur';
    await supabase.from('lead_activity').insert({
      lead_id: leadId,
      user_id: currentUser?.id,
      user_name: userName,
      type: 'assignment',
      payload_json: { to: member ? `${member.first_name} ${member.last_name}` : 'Non assigne' },
    });
  };

  const handleToggleTop = async (leadId: string) => {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead) return;
    const newVal = !lead.is_top_contact;
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, is_top_contact: newVal } : l)));
    if (selectedLead?.id === leadId) setSelectedLead((prev) => prev ? { ...prev, is_top_contact: newVal } : prev);
    await supabase.from('leads').update({ is_top_contact: newVal }).eq('id', leadId);
  };

  const handleNoteSave = async (leadId: string, notes: string) => {
    await supabase.from('leads').update({ notes }).eq('id', leadId);

    const userName = [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ') || 'Utilisateur';
    await supabase.from('lead_activity').insert({
      lead_id: leadId,
      user_id: currentUser?.id,
      user_name: userName,
      type: 'note',
      payload_json: { text: notes.slice(0, 100) },
    });
  };

  const handleExportCSV = () => {
    const selected = leads.filter((l) => selectedIds.has(l.id));
    const rows = selected.map((l) => [
      l.company_name, l.contact_first_name, l.contact_last_name, l.job_title,
      l.email, l.linkedin_url, l.score, l.stage,
    ]);
    const header = ['Entreprise', 'Prenom', 'Nom', 'Poste', 'Email', 'LinkedIn', 'Score', 'Stage'];
    const csv = [header, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkAssign = async (ownerId: string) => {
    const ids = Array.from(selectedIds);
    await supabase.from('leads').update({ owner_id: ownerId }).in('id', ids);
    setSelectedIds(new Set());
    loadAll();
  };

  const toggleFilter = (filter: string) => {
    setActiveFilters((prev) =>
      prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter]
    );
  };

  const filteredLeads = useMemo(() => {
    let result = [...leads];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (l) =>
          l.company_name?.toLowerCase().includes(q) ||
          l.contact_first_name?.toLowerCase().includes(q) ||
          l.contact_last_name?.toLowerCase().includes(q) ||
          l.job_title?.toLowerCase().includes(q)
      );
    }

    for (const f of activeFilters) {
      if (f === 'score80') result = result.filter((l) => l.score >= 80);
      else if (f === 'score70') result = result.filter((l) => l.score >= 70);
      else if (f === 'noEmail') result = result.filter((l) => !l.email);
      else if (f === 'topOnly') result = result.filter((l) => l.is_top_contact);
      else if (f === 'france') result = result.filter((l) => l.company_country?.toLowerCase().includes('france'));
      else if (['new', 'contacted', 'replied', 'meeting', 'deal'].includes(f))
        result = result.filter((l) => l.stage === f);
    }

    return result;
  }, [leads, searchQuery, activeFilters]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-white/10 border-t-white/60" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center">
            <Crosshair className="w-6 h-6 text-white/60" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Prospection Influence</h1>
            <p className="text-white/50">Trouver les decideurs influence, les contacter, closer</p>
          </div>
        </div>
        <button
          onClick={() => setShowSearchModal(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-white text-black rounded-xl font-semibold hover:bg-white/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nouvelle recherche
        </button>
      </div>

      {searches.some((s) => s.status === 'running') && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.06] border border-white/[0.08] rounded-xl text-sm text-white/60">
          <Loader2 className="w-4 h-4 animate-spin" />
          Recherche en cours... Les leads apparaitront automatiquement.
        </div>
      )}

      <LeadFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        activeFilters={activeFilters}
        onToggleFilter={toggleFilter}
      />

      <div className="text-xs text-white/30">
        {filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''} affiche{filteredLeads.length !== 1 ? 's' : ''}
      </div>

      <LeadsTable
        leads={filteredLeads}
        teamMembers={teamMembers}
        selectedIds={selectedIds}
        onToggleSelect={(id) =>
          setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          })
        }
        onSelectAll={() =>
          setSelectedIds((prev) =>
            prev.size === filteredLeads.length
              ? new Set()
              : new Set(filteredLeads.map((l) => l.id))
          )
        }
        onLeadClick={setSelectedLead}
        onStageChange={handleStageChange}
        onOwnerChange={handleOwnerChange}
        onExportCSV={handleExportCSV}
        onBulkAssign={handleBulkAssign}
      />

      <LeadSidePanel
        lead={selectedLead}
        onClose={() => setSelectedLead(null)}
        onToggleTop={handleToggleTop}
        onStageChange={handleStageChange}
        onNoteSave={handleNoteSave}
        agencyId={agencyId}
      />

      <SearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        onSubmit={handleStartSearch}
        isSubmitting={isSubmittingSearch}
      />
    </div>
  );
}
